import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  CloudQueue as CloudIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useApiClient } from '@/hooks/useApiClient';

const AdminSystemHealthPage: React.FC = () => {
  useApiClient(); // Inject current OIDC token into API client
  // Mock data for demonstration
  const systemHealth = {
    overall: 'healthy' as 'healthy' | 'warning' | 'critical',
    uptime: '15 dagar, 7 timmar',
    lastChecked: '2024-08-29 19:52:00',
    
    database: {
      status: 'healthy' as 'healthy' | 'warning' | 'critical',
      responseTime: 15,
      connections: 12,
      maxConnections: 100,
      version: 'PostgreSQL 15.3',
    },
    
    storage: {
      status: 'warning' as 'healthy' | 'warning' | 'critical',
      usedSpace: 750,
      totalSpace: 1000,
      usagePercentage: 75,
    },
    
    api: {
      status: 'healthy' as 'healthy' | 'warning' | 'critical',
      averageResponseTime: 145,
      requestsPerMinute: 23,
      errorRate: 0.2,
    },
    
    background: {
      status: 'healthy' as 'healthy' | 'warning' | 'critical',
      activeJobs: 3,
      queuedJobs: 0,
      failedJobs: 1,
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'critical':
        return <ErrorIcon color="error" />;
      default:
        return <CheckIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success' as const;
      case 'warning':
        return 'warning' as const;
      case 'critical':
        return 'error' as const;
      default:
        return 'default' as const;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'Frisk';
      case 'warning':
        return 'Varning';
      case 'critical':
        return 'Kritisk';
      default:
        return 'Okänd';
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3
        }}>
        <Typography variant="h4" component="h1">
          Systemhälsa
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          size="large"
        >
          Uppdatera status
        </Button>
      </Box>

      {/* Overall Status Alert */}
      {systemHealth.overall === 'warning' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Systemet fungerar men vissa komponenter behöver uppmärksamhet. Kontrollera lagringsutrymmet.
          </Typography>
        </Alert>
      )}

      {/* System Overview */}
      <Grid container spacing={3} sx={{
        mb: 4
      }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{
                mb: 2
              }}>
                Systemöversikt
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1
                    }}>
                    {getStatusIcon(systemHealth.overall)}
                    <Typography variant="body1" sx={{
                      fontWeight: "medium"
                    }}>
                      Övergripande status: {getStatusText(systemHealth.overall)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Systemets allmänna hälsostatus
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mb: 1
                    }}>
                    Drifttid
                  </Typography>
                  <Typography variant="h6">
                    {systemHealth.uptime}
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>
                    Senast kontrollerad: {systemHealth.lastChecked}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{
                mb: 2
              }}>
                Snabbstatus
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(systemHealth.database.status)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Databas" 
                    secondary={`${systemHealth.database.responseTime}ms svarstid`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(systemHealth.storage.status)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Lagring" 
                    secondary={`${systemHealth.storage.usagePercentage}% använt`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(systemHealth.api.status)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="API" 
                    secondary={`${systemHealth.api.averageResponseTime}ms medeltid`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Status Cards */}
      <Grid container spacing={3}>
        {/* Database Status */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2
                }}>
                <StorageIcon color="primary" />
                <Typography variant="h6">Databas</Typography>
                <Chip 
                  label={getStatusText(systemHealth.database.status)}
                  color={getStatusColor(systemHealth.database.status)}
                  size="small"
                />
              </Box>
              
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Svarstid" 
                    secondary={`${systemHealth.database.responseTime}ms`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Aktiva anslutningar" 
                    secondary={`${systemHealth.database.connections}/${systemHealth.database.maxConnections}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Version" 
                    secondary={systemHealth.database.version}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Storage Status */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2
                }}>
                <CloudIcon color="primary" />
                <Typography variant="h6">Lagring</Typography>
                <Chip 
                  label={getStatusText(systemHealth.storage.status)}
                  color={getStatusColor(systemHealth.storage.status)}
                  size="small"
                />
              </Box>
              
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 1
                }}>
                Använt utrymme: {systemHealth.storage.usedSpace}GB av {systemHealth.storage.totalSpace}GB
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={systemHealth.storage.usagePercentage} 
                color={systemHealth.storage.usagePercentage > 80 ? 'warning' : 'primary'}
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
              />
              
              <Typography variant="body2">
                {systemHealth.storage.usagePercentage}% använt
              </Typography>
              
              {systemHealth.storage.usagePercentage > 80 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Lagringsutrymmet börjar ta slut. Överväg att rensa gamla filer eller utöka lagringen.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* API Performance */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2
                }}>
                <SpeedIcon color="primary" />
                <Typography variant="h6">API Prestanda</Typography>
                <Chip 
                  label={getStatusText(systemHealth.api.status)}
                  color={getStatusColor(systemHealth.api.status)}
                  size="small"
                />
              </Box>
              
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Genomsnittlig svarstid" 
                    secondary={`${systemHealth.api.averageResponseTime}ms`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Förfrågningar per minut" 
                    secondary={systemHealth.api.requestsPerMinute}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Felfrekvens" 
                    secondary={`${systemHealth.api.errorRate}%`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Background Jobs */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2
                }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6">Bakgrundsjobb</Typography>
                <Chip 
                  label={getStatusText(systemHealth.background.status)}
                  color={getStatusColor(systemHealth.background.status)}
                  size="small"
                />
              </Box>
              
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Aktiva jobb" 
                    secondary={systemHealth.background.activeJobs}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Köade jobb" 
                    secondary={systemHealth.background.queuedJobs}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Misslyckade jobb" 
                    secondary={systemHealth.background.failedJobs}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminSystemHealthPage;